import { useQuery } from '@tanstack/vue-query';
import { queries } from '~/keys';
import type { Entitlements, SubscriptionPlan, SubscriptionInterval } from '~/utils/billing/entitlements';

export type BillingMe = {
  // ID-181: set while the user holds a seat in a live agency workspace.
  b2bSeat?: { agencyName: string; role: string } | null;
  subscriptionPlan: SubscriptionPlan | null;
  subscriptionInterval: SubscriptionInterval | null;
  subscriptionStatus: string | null;
  subscriptionProvider: string | null;
  subscriptionCurrentPeriodEnd: string | null;
  subscriptionCancelAtPeriodEnd: boolean;
  masterclassPaidAt: string | null;
  legacyUnlimitedTools: boolean;
  trialActive: boolean | null;
  trialEndsAt: string | null;
  entitlements: Entitlements;
  isNewFreePlanUser: boolean;
  isTeamOwner: boolean;
  // Team plan membership: OWNER for the payer, MEMBER (with the owner's name) for seat holders.
  team?: { role: 'OWNER' | 'MEMBER'; ownerName: string | null } | null;
  // Master paywall flag — true once tier enforcement is live (dormant until B7).
  tierGatingEnabled: boolean;
  // UAE programme (ID-106) — present for UAE agents only.
  market?: 'UAE' | 'INT' | 'UNKNOWN';
  uaeAccessLevel?: 'SIGNED_UP' | 'VERIFIED' | 'JOINED' | null;
  limits?: {
    aiQuestions: { used: number; limit: number; remaining: number };
    marketingPosts: { used: number; limit: number; remaining: number };
  } | null;
  nextStep?: 'verify_id' | 'join' | null;
  showPricing?: boolean;
  // In-app purchases (ID-39): who bills the plan and where to manage it.
  store?: {
    managedBy: 'app_store' | 'google_play' | 'web' | null;
    manageUrl: string | null;
    canPurchaseInApp: boolean;
    blockedReason: 'active_web_subscription' | 'team_seat' | 'team_owner' | null;
  };
};

export const useBillingMeQuery = () => {
  const authStore = useAuthStore();
  return useQuery({
    ...queries.billing.me,
    queryFn: () =>
      $fetch<{ success: boolean; data: BillingMe }>('/api/billing/me', {
        headers: { Authorization: `Bearer ${authStore.token}` },
      }).then((r) => r.data),
    enabled: computed(() => !!authStore.token),
    retry: false,
  });
};

// ID-241: the UAE Team seat offer an account manager sent this user.
export type UaeTeamOfferView = {
  offer: { seats: number; seatUsd: number; monthlyUsd: number; offeredAt: string } | null;
  active: boolean;
  otherAccount?: string | null;
};

export const useUaeTeamOfferQuery = (offerId: MaybeRef<string | null>) => {
  const authStore = useAuthStore();
  return useQuery({
    queryKey: computed(() => [...queries.billing.uaeTeamOffer.queryKey, unref(offerId)]),
    queryFn: () =>
      $fetch<{ success: boolean; data: UaeTeamOfferView }>('/api/billing/uae-team/offer', {
        headers: { Authorization: `Bearer ${authStore.token}` },
        query: unref(offerId) ? { offer: unref(offerId) } : undefined,
      }).then((r) => r.data),
    enabled: computed(() => !!authStore.token),
    retry: false,
  });
};
